import { useEffect, useId, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';

import type { SearchFacets } from '../../services/searchApi';
import { compareCalibers } from '../../utils/caliber';
import { toSentenceCase } from '../../utils/labels';
import {
  type MultiValueKey,
  removeFilter,
  setSingleValue,
  toggleMultiValue,
} from '../../utils/searchUrl';

/**
 * Beginner explanations for the most jargon-heavy taxonomy terms (spec §7.4).
 * The full per-term taxonomy descriptions are post-MVP; for now we hardcode a
 * handful so new gun owners aren't lost on the most common acronyms.
 */
const BEGINNER_TOOLTIPS: Record<string, string> = {
  // bullet types
  FMJ: 'FMJ — Full Metal Jacket. Standard range/training ammunition.',
  JHP: 'JHP — Jacketed Hollow Point. Common for defensive handgun ammunition.',
  HP: 'HP — Hollow Point. Expands on impact; often used for defense or hunting.',
  SP: 'SP — Soft Point. Common for hunting; expands more than FMJ.',
  TMJ: 'TMJ — Total Metal Jacket. Fully enclosed bullet; useful indoors.',
  // case materials
  Brass: 'Brass — The standard case material. Reloadable and reliable.',
  Steel: 'Steel — Cheaper case material; some ranges prohibit it.',
  Aluminum: 'Aluminum — Lightweight, non-reloadable; budget range ammunition.',
  Nickel: 'Nickel-plated brass — Smoother feeding; common for defensive carry.',
};

const STOCK_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'in_stock', label: 'In stock' },
  { value: 'limited', label: 'Limited' },
  { value: 'out_of_stock', label: 'Out of stock' },
  { value: 'unknown', label: 'Unknown' },
];

interface SectionDef {
  /** Param key in the URL. */
  paramKey: MultiValueKey;
  /** Human-readable legend. */
  title: string;
  /** Facet key returned by the API (often matches paramKey, sometimes not). */
  facetKey: string;
  /** Apply beginner tooltips to each label? */
  withTooltips?: boolean;
  /** Optional formatter for the bucket label (e.g. "115" → "115 gr"). */
  formatLabel?: (value: string) => string;
  /** Sort buckets numerically by value (default is by API order — usually count). */
  sortNumeric?: boolean;
  /** Sort buckets by caliber bore diameter (sensible ascending order, #109). */
  sortCaliber?: boolean;
  /** Drop a meaningless "Unknown" bucket from the facet (#111). */
  suppressUnknown?: boolean;
  /**
   * Show a type-ahead text input that narrows the option list as you type
   * (#110). Use for facets with many buckets (caliber, brand) where scrolling
   * is painful. The input only appears once the list exceeds FILTER_THRESHOLD.
   */
  filterable?: boolean;
}

/** Show the type-ahead input only once a filterable list is long enough to need it. */
const FILTER_THRESHOLD = 8;

/**
 * Sidebar sections render in declaration order. Grains is placed
 * directly after Bullet type so the most-comparable attributes
 * cluster together — caliber/brand decide WHAT you're shopping for,
 * bullet type + grain decide WHICH variant of that round.
 *
 * The grain facet is intentionally NOT marked withTooltips — the
 * label already includes the unit ("115 gr") via formatGrainLabel.
 */
const SECTIONS: SectionDef[] = [
  // #108: broad ammo type sits first as the widest narrowing axis
  // (handgun/rifle/rimfire/shotgun, cf. AmmoSeek). Facet values are lowercase
  // canonicals — render them in sentence case.
  { paramKey: 'ammoType', title: 'Ammo type', facetKey: 'ammoType', formatLabel: toSentenceCase },
  // #110: caliber + brand have long lists, so they get the type-ahead input.
  {
    paramKey: 'caliber',
    title: 'Caliber',
    facetKey: 'caliber',
    sortCaliber: true,
    filterable: true,
  },
  { paramKey: 'brand', title: 'Brand', facetKey: 'brand', filterable: true },
  // Product line sits directly under Brand: it's the brand's sub-line
  // (Eley → Tenex/Match, Federal → HST/American Eagle) and is most
  // useful once a brand is picked (GitHub #8). The facet still renders
  // standalone — the facet distribution simply narrows as the brand
  // filter narrows the result set.
  { paramKey: 'productLine', title: 'Product line', facetKey: 'productLine' },
  { paramKey: 'bulletType', title: 'Bullet type', facetKey: 'bulletType', withTooltips: true },
  {
    paramKey: 'bulletWeightGrains',
    title: 'Grain (bullet weight)',
    facetKey: 'bulletWeightGrains',
    formatLabel: (v) => `${v} gr`,
    sortNumeric: true,
  },
  {
    paramKey: 'caseMaterial',
    title: 'Case material',
    facetKey: 'caseMaterial',
    withTooltips: true,
    suppressUnknown: true,
  },
  // #112: use-case canonicals are lowercase-hyphenated ("defensive-handgun");
  // render them in sentence case.
  { paramKey: 'useCases', title: 'Use case', facetKey: 'useCases', formatLabel: toSentenceCase },
  { paramKey: 'retailerId', title: 'Retailer', facetKey: 'retailerName' },
];

export interface FilterSidebarProps {
  facets: SearchFacets;
  searchParams: URLSearchParams;
  onChange: (next: URLSearchParams) => void;
}

interface FacetCheckboxListProps {
  paramKey: MultiValueKey;
  /** Human-readable section title, used for the type-ahead input's label. */
  title: string;
  facetKey: string;
  facets: SearchFacets;
  searchParams: URLSearchParams;
  onChange: (next: URLSearchParams) => void;
  withTooltips?: boolean;
  formatLabel?: (value: string) => string;
  sortNumeric?: boolean;
  sortCaliber?: boolean;
  suppressUnknown?: boolean;
  filterable?: boolean;
}

/** Checkbox list for one multi-select facet, rendered inside an accordion. */
function FacetCheckboxList({
  paramKey,
  title,
  facetKey,
  facets,
  searchParams,
  onChange,
  withTooltips,
  formatLabel,
  sortNumeric,
  sortCaliber,
  suppressUnknown,
  filterable,
}: FacetCheckboxListProps) {
  // #110: type-ahead text that narrows long option lists (caliber, brand) as
  // the user types, instead of forcing a scroll through every bucket.
  const [filterText, setFilterText] = useState('');
  const filterFieldId = useId();
  // #111: the normalizer stores an "Unknown" case material as a low-confidence
  // fallback, which surfaces as a useless filter option. Drop it from the facet
  // (the listings still exist — they're just not filterable by "Unknown").
  const rawBuckets = (facets[facetKey] ?? []).filter(
    (b) => !suppressUnknown || b.value.trim().toLowerCase() !== 'unknown'
  );
  let buckets = rawBuckets;
  if (sortNumeric) {
    buckets = [...rawBuckets].sort((a, b) => Number(a.value) - Number(b.value));
  } else if (sortCaliber) {
    buckets = [...rawBuckets].sort((a, b) => compareCalibers(a.value, b.value));
  }
  const selected = new Set(searchParams.getAll(paramKey));
  // Always include any selected values that aren't in the current facet
  // distribution so the user can still see and uncheck them.
  const extras = Array.from(selected).filter((v) => !buckets.some((b) => b.value === v));
  const fmt = formatLabel ?? ((v: string) => v);

  function onToggle(value: string) {
    onChange(toggleMultiValue(searchParams, paramKey, value));
  }

  // #110: only surface the type-ahead once the list is long enough to be
  // painful to scroll. A checked option always stays visible (so it can be
  // unchecked) even when it doesn't match the current query.
  const showFilter = Boolean(filterable) && rawBuckets.length > FILTER_THRESHOLD;
  const query = filterText.trim().toLowerCase();
  const visibleBuckets =
    showFilter && query
      ? buckets.filter((b) => selected.has(b.value) || fmt(b.value).toLowerCase().includes(query))
      : buckets;

  if (buckets.length === 0 && extras.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No options.
      </Typography>
    );
  }

  return (
    <FormControl component="fieldset" variant="standard" fullWidth>
      <Box component="legend" sx={{ position: 'absolute', left: -10_000, top: 'auto' }}>
        {paramKey}
      </Box>
      {showFilter ? (
        <>
          <TextField
            id={filterFieldId}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            size="small"
            fullWidth
            label={`Filter ${title.toLowerCase()}`}
            placeholder="Type to filter…"
            autoComplete="off"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1 }}
          />
          {/* Live count so screen-reader users hear the list narrow as they type. */}
          <Box aria-live="polite" sx={{ position: 'absolute', left: -10_000, top: 'auto' }}>
            {query ? `${visibleBuckets.length} matching ${title.toLowerCase()} options` : ''}
          </Box>
        </>
      ) : null}
      {showFilter && query && visibleBuckets.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No matches.
        </Typography>
      ) : null}
      <FormGroup>
        {visibleBuckets.map((bucket) => {
          const labelText = `${fmt(bucket.value)} (${bucket.count})`;
          return (
            <Box key={bucket.value} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.has(bucket.value)}
                    onChange={() => onToggle(bucket.value)}
                    inputProps={{ 'aria-label': labelText }}
                  />
                }
                label={labelText}
              />
              {withTooltips && BEGINNER_TOOLTIPS[bucket.value] ? (
                <Tooltip title={BEGINNER_TOOLTIPS[bucket.value]} arrow>
                  <IconButton
                    size="small"
                    aria-label={`What is ${bucket.value}?`}
                    sx={{ ml: -0.5 }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          );
        })}
        {extras.map((value) => (
          <FormControlLabel
            key={value}
            control={
              <Checkbox
                checked
                onChange={() => onToggle(value)}
                inputProps={{ 'aria-label': fmt(value) }}
              />
            }
            label={fmt(value)}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

interface SidebarBodyProps extends FilterSidebarProps {
  /** Stable IDs so labels and ARIA references stay in sync across renders. */
  idBase: string;
}

function SidebarBody({ facets, searchParams, onChange, idBase }: SidebarBodyProps) {
  const inStockOnly = searchParams.get('inStockOnly') === 'true';
  const excludeReman = searchParams.get('excludeRemanufactured') === 'true';
  const lotNumber = searchParams.get('lotNumber') ?? '';
  const minRounds = Number(searchParams.get('minRoundCount') ?? '');
  const minCpr = Number(searchParams.get('minPricePerRound') ?? '');
  const maxCpr = Number(searchParams.get('maxPricePerRound') ?? '');

  function setBool(key: string, value: boolean) {
    if (value) {
      onChange(setSingleValue(searchParams, key, 'true'));
    } else {
      onChange(removeFilter(searchParams, key));
    }
  }

  function setRange(
    key: 'minPricePerRound' | 'maxPricePerRound' | 'minRoundCount',
    value: number | undefined
  ) {
    if (value == null || !Number.isFinite(value)) {
      onChange(removeFilter(searchParams, key));
    } else {
      onChange(setSingleValue(searchParams, key, String(value)));
    }
  }

  function setStock(value: string) {
    onChange(setSingleValue(searchParams, 'stockStatus', value || undefined));
  }

  const stockStatus = searchParams.get('stockStatus') ?? '';

  // Product line options are a brand's sub-lines, so they're only meaningful
  // once a brand is chosen. Hide the section until at least one brand is
  // selected; the FilterSidebar effect clears any stale productLine values when
  // the last brand is removed (GitHub #8).
  const hasBrandSelected = searchParams.getAll('brand').length > 0;
  const visibleSections = SECTIONS.filter(
    (section) => section.paramKey !== 'productLine' || hasBrandSelected
  );

  return (
    <Box
      role="region"
      aria-label="Search filters"
      sx={{ width: { xs: 300, md: 280 }, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Typography variant="h2" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>
        Filters
      </Typography>

      {visibleSections.map((section) => (
        <Accordion
          key={section.paramKey}
          defaultExpanded={section.paramKey === 'caliber'}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${idBase}-${section.paramKey}-content`}
            id={`${idBase}-${section.paramKey}-header`}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails id={`${idBase}-${section.paramKey}-content`}>
            <FacetCheckboxList
              paramKey={section.paramKey}
              title={section.title}
              facetKey={section.facetKey}
              facets={facets}
              searchParams={searchParams}
              onChange={onChange}
              withTooltips={section.withTooltips}
              formatLabel={section.formatLabel}
              sortNumeric={section.sortNumeric}
              sortCaliber={section.sortCaliber}
              suppressUnknown={section.suppressUnknown}
              filterable={section.filterable}
            />
          </AccordionDetails>
        </Accordion>
      ))}

      <Accordion disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${idBase}-stock-content`}
          id={`${idBase}-stock-header`}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Stock status
          </Typography>
        </AccordionSummary>
        <AccordionDetails id={`${idBase}-stock-content`}>
          <FormControl component="fieldset" variant="standard" fullWidth>
            <Box component="legend" sx={{ position: 'absolute', left: -10_000, top: 'auto' }}>
              Stock status
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inStockOnly}
                  onChange={(e) => setBool('inStockOnly', e.target.checked)}
                  inputProps={{ 'aria-label': 'In stock only' }}
                />
              }
              label="In stock only"
            />
            <FormGroup>
              {STOCK_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={stockStatus === opt.value}
                      onChange={() => setStock(stockStatus === opt.value ? '' : opt.value)}
                      inputProps={{ 'aria-label': opt.label }}
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${idBase}-ranges-content`}
          id={`${idBase}-ranges-header`}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Round count &amp; price
          </Typography>
        </AccordionSummary>
        <AccordionDetails id={`${idBase}-ranges-content`}>
          <Stack spacing={3}>
            <Box>
              <Typography id={`${idBase}-min-rounds`} variant="body2" sx={{ mb: 1 }}>
                Minimum round count:{' '}
                {Number.isFinite(minRounds) && minRounds > 0 ? minRounds : 'any'}
              </Typography>
              <Slider
                aria-labelledby={`${idBase}-min-rounds`}
                value={Number.isFinite(minRounds) && minRounds > 0 ? minRounds : 0}
                onChange={(_e, v) =>
                  setRange('minRoundCount', Array.isArray(v) ? v[0] : v || undefined)
                }
                min={0}
                max={5000}
                step={50}
                valueLabelDisplay="auto"
              />
            </Box>
            <Box>
              <Typography id={`${idBase}-min-cpr`} variant="body2" sx={{ mb: 1 }}>
                Minimum price per round
              </Typography>
              <TextField
                aria-labelledby={`${idBase}-min-cpr`}
                type="number"
                size="small"
                inputProps={{ min: 0, step: '0.01' }}
                value={Number.isFinite(minCpr) ? minCpr : ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? undefined : Number(e.target.value);
                  setRange('minPricePerRound', v);
                }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Box>
            <Box>
              <Typography id={`${idBase}-max-cpr`} variant="body2" sx={{ mb: 1 }}>
                Maximum price per round
              </Typography>
              <TextField
                aria-labelledby={`${idBase}-max-cpr`}
                type="number"
                size="small"
                inputProps={{ min: 0, step: '0.01' }}
                value={Number.isFinite(maxCpr) ? maxCpr : ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? undefined : Number(e.target.value);
                  setRange('maxPricePerRound', v);
                }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${idBase}-misc-content`}
          id={`${idBase}-misc-header`}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            More options
          </Typography>
        </AccordionSummary>
        <AccordionDetails id={`${idBase}-misc-content`}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeReman}
                  onChange={(e) => setBool('excludeRemanufactured', e.target.checked)}
                  inputProps={{ 'aria-label': 'Exclude remanufactured' }}
                />
              }
              label="Exclude remanufactured"
            />
          </FormGroup>
          <TextField
            label="Lot number"
            value={lotNumber}
            onChange={(e) => onChange(setSingleValue(searchParams, 'lotNumber', e.target.value))}
            size="small"
            fullWidth
            sx={{ mt: 2 }}
            helperText="Exact match — used by competitive shooters."
            InputLabelProps={{ shrink: true }}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

/**
 * Filter sidebar (spec §15.3 / §7.4).
 *
 * Renders inline on desktop; on mobile, renders a "Filters" button that
 * opens a Drawer containing the same body. The URL is the source of
 * truth — every control writes through `onChange` to the parent, which
 * pushes the change back to `useSearchParams`.
 */
export function FilterSidebar(props: FilterSidebarProps) {
  const reactId = useId();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Product line is scoped to a chosen brand. When the last brand is
  // deselected, drop any orphaned productLine filters so they don't linger as
  // an invisible, applied filter (the section itself is hidden in SidebarBody).
  const { searchParams, onChange } = props;
  const brandCount = searchParams.getAll('brand').length;
  const productLineCount = searchParams.getAll('productLine').length;
  useEffect(() => {
    if (brandCount === 0 && productLineCount > 0) {
      const next = new URLSearchParams(searchParams);
      next.delete('productLine');
      onChange(next);
    }
  }, [brandCount, productLineCount, searchParams, onChange]);

  const body = <SidebarBody {...props} idBase={reactId} />;

  return (
    <>
      {/* Desktop: inline. */}
      <Box
        component="aside"
        aria-label="Search filters"
        sx={{
          display: { xs: 'none', md: 'block' },
          borderRight: 1,
          borderColor: 'divider',
        }}
      >
        {body}
      </Box>

      {/* Mobile: drawer trigger + drawer. */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }}>
        <IconButton
          onClick={() => setDrawerOpen(true)}
          aria-label="Open filters"
          aria-expanded={drawerOpen}
          aria-controls="mobile-filter-drawer"
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 2 }}
        >
          <Typography variant="button">Filters</Typography>
        </IconButton>
        <Drawer
          id="mobile-filter-drawer"
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close filters">
              <CloseIcon />
            </IconButton>
          </Box>
          {body}
        </Drawer>
      </Box>
    </>
  );
}

export default FilterSidebar;
